import React, { useEffect } from 'react';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function getScoreColor(s) {
  if (s >= 85) return '#10F090';
  if (s >= 70) return '#22D3EE';
  if (s >= 50) return '#F59E0B';
  return '#B5325B';
}

function getMF(v) {
  if (!v) return { text: '—', color: 'var(--text-4, #94a3b8)' };
  if (v >= 70) return { text: 'High',   color: 'var(--green-text,  #16a34a)' };
  if (v >= 50) return { text: 'Medium', color: 'var(--orange-text, #d97706)' };
  return             { text: 'Low',    color: '#F59E0B' };
}

/* ─── Icons ─────────────────────────────────────────────────────────────── */
const IcoBar = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2"    y="6" width="3" height="8"  rx="0.8"/>
    <rect x="6.5"  y="3" width="3" height="11" rx="0.8"/>
    <rect x="11"   y="1" width="3" height="13" rx="0.8"/>
  </svg>
);
const IcoAlert = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2L1.5 13h13L8 2z"/>
    <path d="M8 7v3M8 11.5v.5"/>
  </svg>
);
const IcoTrend = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12l4-5 3 3 5-7"/>
  </svg>
);
const IcoTarget = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6"/>
    <circle cx="8" cy="8" r="2"/>
    <path d="M8 2v2M8 12v2M2 8h2M12 8h2"/>
  </svg>
);

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const METRICS_CSS = `
  /* grid of 4 metric cards */
  .db-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
  }

  /* individual card */
  .mc {
    background: var(--card, #ffffff);
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 16px;
    padding: 20px 20px 16px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    min-width: 0;            /* prevents blowout inside a grid */
  }

  /* label row */
  .mc-lbl {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-4, #94a3b8);
    letter-spacing: 0.03em;
    line-height: 1.3;
    margin-right: 8px;       /* breathing room before icon */
  }

  /* icon bubble */
  .mc-ico {
    width: 32px; height: 32px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .i-blue   { background: rgba(0,130,251,0.10);   color: var(--blue, #0082FB); }
  .i-orange { background: rgba(245,158,11,0.10);  color: var(--orange-text, #d97706); }
  .i-green  { background: rgba(16,240,144,0.10);  color: var(--green-text,  #16a34a); }
  .i-yellow { background: rgba(245,158,11,0.10);  color: #F59E0B; }

  /* big number / text */
  .mc-num {
    font-family: 'Sansation', system-ui, sans-serif;
    font-size: clamp(28px, 4vw, 40px);
    font-weight: 700;
    line-height: 1;
    margin: 8px 0 6px;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* sub label below number */
  .mc-sub {
    font-size: 11.5px;
    color: var(--text-4, #94a3b8);
    line-height: 1.4;
    margin-bottom: 14px;
    flex: 1;               /* pushes footer to bottom */
    min-height: 0;
  }

  /* footer key-value pairs */
  .mc-foot {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    padding-top: 12px;
    border-top: 1px solid var(--border, #e2e8f0);
  }
  .mc-kv { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .mc-k  { font-size: 10px; font-weight: 600; color: var(--text-4, #94a3b8); letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap; }
  .mc-v  {
    font-size: 12px; font-weight: 700;
    color: var(--foreground, #0f172a);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    max-width: 100px;
  }
  .mc-v.sm    { font-size: 11px; max-width: 90px; }
  .mc-v.green { color: var(--green-text,  #16a34a); }
  .mc-v.orange{ color: var(--orange-text, #d97706); }
  .mc-v.blue  { color: var(--blue, #0082FB); }
  .suf { font-size: 10px; font-weight: 500; opacity: 0.7; margin-left: 1px; }

  /* ── 2 columns on tablets ── */
  @media (max-width: 900px) {
    .db-metrics {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* ── 2 columns, tighter on large phones ── */
  @media (max-width: 640px) {
    .db-metrics {
      gap: 12px;
    }
    .mc {
      padding: 16px 14px 12px;
      border-radius: 14px;
    }
    .mc-ico { width: 28px; height: 28px; border-radius: 7px; }
    .mc-ico svg { width: 15px; height: 15px; }
    .mc-sub { font-size: 11px; margin-bottom: 10px; }
    .mc-foot { gap: 4px 10px; padding-top: 10px; }
    .mc-v { max-width: 80px; }
    .mc-v.sm { max-width: 70px; font-size: 10.5px; }
  }

  /* ── single column on very small phones ── */
  @media (max-width: 380px) {
    .db-metrics {
      grid-template-columns: 1fr;
    }
    .mc { padding: 14px 12px 10px; }
  }
`;

const STYLE_ID = 'mc-style';

/* ─── MetricsCard ────────────────────────────────────────────────────────── */
const MetricsCard = ({ activeReport, allItems, completed, queueCount, rd, formatDisplayDate }) => {
  /* inject styles once */
  useEffect(() => {
    if (!document.getElementById(STYLE_ID)) {
      const s = document.createElement('style');
      s.id = STYLE_ID;
      s.textContent = METRICS_CSS;
      document.head.appendChild(s);
    }
  }, []);

  const avgScore = completed.length > 0
    ? Math.round(completed.reduce((a, r) => a + (r.decxScore || 0), 0) / completed.length)
    : 0;

  const pActions = rd?.priorityActions ?? 0;
  const mfVal    = rd?.marketFitScore  ?? 0;
  const mf       = getMF(activeReport ? mfVal : null);

  const bestScore = completed.length > 0
    ? Math.max(...completed.map(r => r.decxScore || 0))
    : 0;

  return (
    <div className="db-metrics">

      {/* ── Slides Analyzed ── */}
      <div className="mc">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <span className="mc-lbl">Slides Analyzed</span>
          <div className="mc-ico i-blue"><IcoBar /></div>
        </div>

        <div className="mc-num" style={{ color: 'var(--blue, #0082FB)' }}>
          {activeReport
            ? activeReport.slidesAnalyzed
            : (completed[0]?.slidesAnalyzed || '—')}
        </div>

        <div className="mc-sub">
          {activeReport
            ? 'Current report slides'
            : completed.length > 0
              ? 'Latest report slides'
              : 'No slides analyzed yet'}
        </div>

        <div className="mc-foot">
          <div className="mc-kv">
            <div className="mc-k">Submissions</div>
            <div className="mc-v">{allItems.length}</div>
          </div>
          <div className="mc-kv">
            <div className="mc-k">Done</div>
            <div className="mc-v green">{completed.length}</div>
          </div>
          <div className="mc-kv">
            <div className="mc-k">Queue</div>
            <div className={`mc-v${queueCount > 0 ? ' orange' : ''}`}>{queueCount}</div>
          </div>
        </div>
      </div>

      {/* ── Priority Actions ── */}
      <div className="mc">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <span className="mc-lbl">Priority Actions</span>
          <div className={`mc-ico ${pActions === 0 ? 'i-green' : 'i-orange'}`}><IcoAlert /></div>
        </div>

        <div
          className="mc-num"
          style={{
            color: activeReport
              ? (pActions === 0 ? 'var(--green-text, #16a34a)' : 'var(--orange-text, #d97706)')
              : 'var(--text-4, #94a3b8)',
          }}
        >
          {activeReport ? pActions : '—'}
        </div>

        <div className="mc-sub">
          {!activeReport
            ? 'Complete an audit first'
            : pActions === 0
              ? 'No urgent issues found'
              : `${pActions} high-priority issue${pActions !== 1 ? 's' : ''}`}
        </div>

        <div className="mc-foot">
          <div className="mc-kv">
            <div className="mc-k">Latest Report</div>
            <div className="mc-v sm">{activeReport?.presentationName || '—'}</div>
          </div>
          {activeReport && (
            <div className="mc-kv">
              <div className="mc-k">Date</div>
              <div className="mc-v sm">{formatDisplayDate(activeReport.uploadDate)}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Avg DecX Score ── */}
      <div className="mc">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <span className="mc-lbl">Avg DecX Score</span>
          <div className="mc-ico i-green"><IcoTrend /></div>
        </div>

        <div
          className="mc-num"
          style={{ color: avgScore > 0 ? getScoreColor(avgScore) : 'var(--text-4, #94a3b8)' }}
        >
          {avgScore > 0 ? avgScore : '—'}
        </div>

        <div className="mc-sub">
          {completed.length > 0
            ? `Across ${completed.length} completed audit${completed.length !== 1 ? 's' : ''}`
            : 'No completed audits yet'}
        </div>

        <div className="mc-foot">
          <div className="mc-kv">
            <div className="mc-k">Latest</div>
            <div className="mc-v" style={{ color: activeReport ? getScoreColor(activeReport.decxScore) : 'var(--text-4, #94a3b8)' }}>
              {activeReport ? activeReport.decxScore : '—'}
              {activeReport && <span className="suf">/100</span>}
            </div>
          </div>
          {completed.length > 0 && (
            <div className="mc-kv">
              <div className="mc-k">Best</div>
              <div className="mc-v" style={{ color: getScoreColor(bestScore) }}>
                {bestScore}<span className="suf">/100</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Market Fit ── */}
      <div className="mc">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <span className="mc-lbl">Market Fit</span>
          <div className="mc-ico i-yellow"><IcoTarget /></div>
        </div>

        <div
          className="mc-num"
          style={{ color: mf.color, fontSize: 'clamp(24px, 3.5vw, 32px)', letterSpacing: '-0.02em' }}
        >
          {mf.text}
        </div>

        <div className="mc-sub">
          {activeReport ? `Industry avg: ${mfVal}/100` : 'Complete an audit to see'}
        </div>

        <div className="mc-foot">
          <div className="mc-kv">
            <div className="mc-k">Stage</div>
            <div className="mc-v">{rd?.fundingStage || '—'}</div>
          </div>
          <div className="mc-kv">
            <div className="mc-k">Ask</div>
            <div className="mc-v blue">{rd?.fundingAsk || '—'}</div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MetricsCard;